import poolTableImg from "../assets/images/pool-table.png";
import snTableImg from "../assets/images/snooker-table.png";
import chpoolTableImg from "../assets/images/chpool-table.png";
import uspoolTableImg from "../assets/images/uspool-table.png";
import { dashboardRequest } from "../services/dashboardRequest";
import { useDashboardResource } from "../hooks/useDashboardResource";
import { statisticsTimezone } from "../utils/frameStatisticsDate";
import { useAuth } from "../contexts/AuthContext";
import { useCallback, useEffect, useRef, useState } from "react";
import config from "../config";
import jsPDF from "jspdf";

const Club = () => {
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editType, setEditType] = useState(null);
    const [editPricingType, setEditPricingType] = useState('snooker');
    const { user, isAuthenticated } = useAuth();
    const [saving, setSaving] = useState(false);
    const [editError, setEditError] = useState('');
    const activeUser = useRef(user?.id);
    activeUser.current = user?.id;

    const loadTables = useCallback(async ({ signal }) => {
        const [tablesResponse, statsResponse] = await Promise.all([
            dashboardRequest('/club/tables', { method: 'POST', body: { id: user.id }, signal }),
            dashboardRequest('/club/frames/stats', {
                method: 'POST', body: { id_user: user.id, timezone: statisticsTimezone() }, signal,
            }),
        ]);
        if (!Array.isArray(tablesResponse?.tables) || !Array.isArray(statsResponse?.tables)
            || statsResponse.tables.some(table => !Array.isArray(table.days))) {
            throw new Error('The tables response was incomplete. Please try again.');
        }
        return { tables: tablesResponse.tables, stats: statsResponse.tables };
    }, [user?.id]);
    const { data, loading, refreshing, error, refresh, update } = useDashboardResource({
        key: `club-tables:${user?.id || ''}`, enabled: Boolean(user?.id), load: loadTables,
    });
    const tables = data?.tables || [];
    const statsData = data?.stats || null;

    useEffect(() => {
        activeUser.current = user?.id;
        setOpen(false);
        setEditError('');
        setSaving(false);
        return () => { activeUser.current = null; };
    }, [user?.id]);

    const editTable = async () => {
        if (saving || !user?.id) return;
        const requestUser = user.id;
        setSaving(true);
        setEditError('');
        const payload = {
            id: editId,
            id_user: user.id,
            name: editName
        };
        if (editType === 'snooker') {
            payload.pricing_type = editPricingType;
        }
        try {
            await dashboardRequest('/club/table/edit', { method: 'POST', body: payload });
            if (activeUser.current !== requestUser) return;
            update(previous => ({ ...previous, tables: previous.tables.map(table => table.id === editId
                ? { ...table, name: editName, ...(editType === 'snooker' ? { pricing_type: editPricingType } : {}) }
                : table) }));
            setEditId(null);
            setEditName('');
            setEditType(null);
            setEditPricingType('snooker');
            setOpen(false);
            refresh();
        } catch (requestError) {
            if (activeUser.current === requestUser) setEditError(requestError.message || 'Could not save this table. Please retry.');
        } finally {
            if (activeUser.current === requestUser) setSaving(false);
        }
    };

    const openEditModal = (id, name) => {
        setEditError('');
        setEditId(id);
        setEditName(name);
        const table = tables.find(t => t.id === id);
        setEditType(table ? table.type : null);
        setEditPricingType(table && table.pricing_type ? table.pricing_type : 'snooker');
        setOpen(true);
    };

    const downloadPDF = () => {
        if (!statsData || !user) return;

        const doc = new jsPDF();
        const pageWidth = 210;
        const pageHeight = 297;
        const margin = 10;
        const lineHeight = 6;
        let yPosition = 20;
        let currentPage = 1;

        if (user.logo) {
            doc.addImage(`${config.STORAGE_BASE_URL}/${user.logo}`, 'PNG', (pageWidth - 50) / 2, yPosition - 10, 50, 20);
            yPosition += 30;
        }

        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        const title = `Match Statistics – ${user.name}`;
        const textWidth = doc.getTextWidth(title);
        doc.text(title, (pageWidth - textWidth) / 2, yPosition);
        yPosition += 15;

        statsData.forEach((tableData) => {
            if (yPosition > pageHeight - 40) {
                doc.addPage();
                yPosition = 20;
                currentPage++;
            }

            doc.setFontSize(12);
            doc.text(`Table ${tableData.table_name}`, margin, yPosition);
            yPosition += 10;

            tableData.days.forEach((day) => {
                if (yPosition > pageHeight - 60) {
                    doc.addPage();
                    yPosition = 20;
                    currentPage++;
                }

                doc.setFontSize(10);
                doc.text(`${day.day}: ${day.frame_count} frames (${day.qualified_count || 0} qualified, ${day.not_qualified_count || 0} not qualified)`, margin, yPosition);
                yPosition += 8;

                if (day.frames && day.frames.length > 0) {
                    const headers = ['Player 1', 'P1', 'Breaks P1', 'P2', 'Breaks P2', 'Player 2', 'Status', 'Stat Date'];
                    const colWidths = [23, 12, 18, 12, 18, 23, 32, 42];
                    const startX = margin;
                    let currentX = startX;
                    const tableStartY = yPosition - lineHeight;

                    doc.setFillColor(0, 128, 0);
                    doc.rect(startX, yPosition - lineHeight, pageWidth - 2 * margin, lineHeight, 'F');
                    doc.setTextColor(255, 255, 255);
                    doc.setFontSize(8);
                    headers.forEach((header, index) => {
                        doc.text(header, currentX + 2, yPosition - 1);
                        currentX += colWidths[index];
                    });
                    yPosition += lineHeight;
                    doc.setTextColor(0, 0, 0);

                    doc.setDrawColor(0, 0, 0);
                    doc.line(margin, tableStartY, pageWidth - margin, tableStartY);

                    let dayTableStartY = tableStartY;

                    day.frames.forEach((frame, frameIndex) => {
                        if (yPosition + lineHeight > pageHeight - 20) {
                            drawVerticalLines(doc, dayTableStartY, pageHeight - 10, startX, colWidths, pageWidth, margin);
                            doc.addPage();
                            yPosition = 10;
                            currentPage++;
                            dayTableStartY = yPosition;
                        }

                        currentX = startX;
                        const rowData = [
                            frame.player1 || '',
                            frame.p1?.toString() || '',
                            frame.breaks_p1?.toString() || '',
                            frame.p2?.toString() || '',
                            frame.breaks_p2?.toString() || '',
                            frame.player2 || '',
                            frame.qualification_status || '',
                            frame.stat_date || ''
                        ];

                        rowData.forEach((data, index) => {
                            doc.text(data, currentX + 2, yPosition - 1);
                            currentX += colWidths[index];
                        });
                        yPosition += lineHeight;

                        doc.line(margin, yPosition - lineHeight, pageWidth - margin, yPosition - lineHeight);
                    });

                    drawVerticalLines(doc, dayTableStartY, yPosition - lineHeight, startX, colWidths, pageWidth, margin);

                    yPosition += 5;
                }
            });
            yPosition += 10;
        });

        const totalPages = doc.internal.getNumberOfPages();
        const generatedDate = new Date().toLocaleDateString('en-GB').replace(/\//g, '-') + ' at ' + new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }).replace(':', 'h');

        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(`Generated on: ${generatedDate}`, margin, pageHeight - 10);
        }

        doc.save(`${user.name}_stats_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const drawVerticalLines = (doc, startY, endY, startX, colWidths, pageWidth, margin) => {
        doc.setDrawColor(0, 0, 0);
        let currentX = startX;
        colWidths.forEach((width) => {
            doc.line(currentX, startY, currentX, endY);
            currentX += width;
        });
        doc.line(pageWidth - margin, startY, pageWidth - margin, endY);
    };

    return (
        <div className="page-wrapper">
            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Club</div>
                            {isAuthenticated() ? (
                                <h2 className="page-title">{user ? user.name : ''}</h2>
                            ) : null}
                        </div>
                        <div className="col-auto ms-auto d-print-none">
                            <button type="button" className="btn me-2" onClick={refresh} disabled={!user?.id || loading || refreshing}>
                                {refreshing ? 'Refreshing…' : 'Refresh'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary club-page-primary-action"
                                onClick={downloadPDF}
                                disabled={!statsData || loading || refreshing || Boolean(error)}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="icon"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2"
                                    stroke="currentColor"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M12 4v12" />
                                    <path d="M7 11l5 5l5 -5" />
                                    <path d="M5 20h14" />
                                </svg>
                                Download stats PDF
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="page-body">
                <div className="container-xl">
                    {loading && <div role="status" className="alert alert-info">Loading your tables and statistics…</div>}
                    {error && <div role="alert" className="alert alert-warning">{data
                        ? 'The tables could not be refreshed. Showing the last successful update; please retry.'
                        : 'We could not load your tables and statistics. Please retry.'}</div>}
                    {data && tables.length === 0 && <p>No tables have been added yet.</p>}
                    <div className="row row-deck row-cards mb-3">
                        {tables.filter(t => t.type === 'snooker' && t.pricing_type !== 'mini').length > 0 ? (
                            <h3 className="card-title mt-6">Snooker</h3>
                        ) : null}
                        {tables
                            .filter(t => t.type === 'snooker' && t.pricing_type !== 'mini')
                            .map((table, i) => (
                                <div key={i} className="col-12 col-sm-6 col-md-3 m-0 mb-4">
                                    <div className={`card ${table.deleted_at !== null ? 'opacity-50' : ''} cursor-pointer`}>
                                        <div
                                            className={`card-status-indicator ${table.deleted_at === null ? 'status-available' : 'status-unavailable'}`}
                                        >
                                            {table.deleted_at === null ? 'Available' : 'Not Available'}
                                        </div>
                                        {table.deleted_at === null && (
                                            <span
                                                className="badge badge-outline text-white cursor-pointer edit-badge"
                                                onClick={() => openEditModal(table.id, table.name)}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="icon icon-tabler icons-tabler-outline icon-tabler-pencil"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
                                                    <path d="M13.5 6.5l4 4" />
                                                </svg>
                                            </span>
                                        )}
                                        <div className="card-body text-center">
                                            <div className="mb-3">
                                                <img src={snTableImg} alt="Table img" className="table-img" />
                                            </div>
                                        </div>
                                        <p className="card-btn">{table.name}</p>
                                    </div>
                                </div>
                            ))}
                    </div>
                    <div className="row row-deck row-cards mb-3">
                        {tables.filter(t => t.type === 'snooker' && t.pricing_type === 'mini').length > 0 ? (
                            <h3 className="card-title mt-6">Mini Snooker</h3>
                        ) : null}
                        {tables
                            .filter(t => t.type === 'snooker' && t.pricing_type === 'mini')
                            .map((table, i) => (
                                <div key={i} className="col-12 col-sm-6 col-md-3 m-0 mb-4">
                                    <div className={`card ${table.deleted_at !== null ? 'opacity-50' : ''} cursor-pointer`}>
                                        <div
                                            className={`card-status-indicator ${table.deleted_at === null ? 'status-available' : 'status-unavailable'}`}
                                        >
                                            {table.deleted_at === null ? 'Available' : 'Not Available'}
                                        </div>
                                        {table.deleted_at === null && (
                                            <span
                                                className="badge badge-outline text-white cursor-pointer edit-badge"
                                                onClick={() => openEditModal(table.id, table.name)}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="icon icon-tabler icons-tabler-outline icon-tabler-pencil"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
                                                    <path d="M13.5 6.5l4 4" />
                                                </svg>
                                            </span>
                                        )}
                                        <div className="card-body text-center">
                                            <div className="my-3">
                                                <img src={snTableImg} alt="Table img" className="table-img" style={{ width: '70%' }} />
                                            </div>
                                        </div>
                                        <p className="card-btn">{table.name}</p>
                                    </div>
                                </div>
                            ))}
                    </div>
                    <div className="row row-deck row-cards mb-3">
                        {tables.filter(t => t.type === 'pool').length > 0 ? (
                            <h3 className="card-title mt-6">Pool</h3>
                        ) : null}
                        {tables
                            .filter(t => t.type === 'pool')
                            .map((table, i) => (
                                <div key={i} className="col-12 col-sm-6 col-md-3 m-0 mb-4">
                                    <div className={`card ${table.deleted_at !== null ? 'opacity-50' : ''} cursor-pointer`}>
                                        <div
                                            className={`card-status-indicator ${table.deleted_at === null ? 'status-available' : 'status-unavailable'}`}
                                        >
                                            {table.deleted_at === null ? 'Available' : 'Not Available'}
                                        </div>
                                        {table.deleted_at === null && (
                                            <span
                                                className="badge badge-outline text-white cursor-pointer edit-badge"
                                                onClick={() => openEditModal(table.id, table.name)}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="icon icon-tabler icons-tabler-outline icon-tabler-pencil"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
                                                    <path d="M13.5 6.5l4 4" />
                                                </svg>
                                            </span>
                                        )}
                                        <div className="card-body text-center">
                                            <div className="mb-3">
                                                <img src={poolTableImg} alt="Table img" className="table-img" />
                                            </div>
                                        </div>
                                        <p className="card-btn">{table.name}</p>
                                    </div>
                                </div>
                            ))}
                    </div>
                    <div className="row row-deck row-cards mb-3">
                        {tables.filter(t => t.type === 'us-pool').length > 0 ? (
                            <h3 className="card-title mt-6">Us pool</h3>
                        ) : null}
                        {tables
                            .filter(t => t.type === 'us-pool')
                            .map((table, i) => (
                                <div key={i} className="col-12 col-sm-6 col-md-3 m-0 mb-4">
                                    <div className={`card ${table.deleted_at !== null ? 'opacity-50' : ''} cursor-pointer`}>
                                        <div
                                            className={`card-status-indicator ${table.deleted_at === null ? 'status-available' : 'status-unavailable'}`}
                                        >
                                            {table.deleted_at === null ? 'Available' : 'Not Available'}
                                        </div>
                                        {table.deleted_at === null && (
                                            <span
                                                className="badge badge-outline text-white cursor-pointer edit-badge"
                                                onClick={() => openEditModal(table.id, table.name)}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="icon icon-tabler icons-tabler-outline icon-tabler-pencil"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
                                                    <path d="M13.5 6.5l4 4" />
                                                </svg>
                                            </span>
                                        )}
                                        <div className="card-body text-center">
                                            <div className="mb-3">
                                                <img src={uspoolTableImg} alt="Table img" className="table-img" />
                                            </div>
                                        </div>
                                        <p className="card-btn">{table.name}</p>
                                    </div>
                                </div>
                            ))}
                    </div>
                    <div className="row row-deck row-cards mb-3">
                        {tables.filter(t => t.type === 'chinese-pool').length > 0 ? (
                            <h3 className="card-title mt-6">Hey ball</h3>
                        ) : null}
                        {tables
                            .filter(t => t.type === 'chinese-pool')
                            .map((table, i) => (
                                <div key={i} className="col-12 col-sm-6 col-md-3 m-0 mb-4">
                                    <div className={`card ${table.deleted_at !== null ? 'opacity-50' : ''} cursor-pointer`}>
                                        <div
                                            className={`card-status-indicator ${table.deleted_at === null ? 'status-available' : 'status-unavailable'}`}
                                        >
                                            {table.deleted_at === null ? 'Available' : 'Not Available'}
                                        </div>
                                        {table.deleted_at === null && (
                                            <span
                                                className="badge badge-outline text-white cursor-pointer edit-badge"
                                                onClick={() => openEditModal(table.id, table.name)}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="icon icon-tabler icons-tabler-outline icon-tabler-pencil"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
                                                    <path d="M13.5 6.5l4 4" />
                                                </svg>
                                            </span>
                                        )}
                                        <div className="card-body text-center">
                                            <div className="mb-3">
                                                <img src={chpoolTableImg} alt="Table img" className="table-img" />
                                            </div>
                                        </div>
                                        <p className="card-btn">{table.name}</p>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
            <div
                className={`modal modal-blur fade ${(open) ? 'show' : ''}`}
                id="modal-large"
                tabIndex="-1"
                role="dialog"
                aria-hidden={!open}
                style={open ? { display: 'block' } : { display: 'none' }}
            >
                <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit table</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={() => setOpen(!open)}
                            ></button>
                        </div>
                        <div className="modal-body">
                            {editError && <div role="alert" className="alert alert-warning">{editError}</div>}
                            <div className="row">
                                <div className="col-lg-12">
                                    <input
                                        type="text"
                                        className="form-control form-control mb-2"
                                        name="Form control rounded"
                                        placeholder="Name.."
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                    />
                                </div>
                                {editType === 'snooker' && (
                                    <div className="col-lg-12 mt-2">
                                        <label className="form-label">Pricing Type</label>
                                        <select className="form-select" value={editPricingType} onChange={(e) => setEditPricingType(e.target.value)}>
                                            <option value="snooker">Snooker</option>
                                            <option value="mini">Mini Snooker</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn me-auto"
                                data-bs-dismiss="modal"
                                onClick={() => setOpen(!open)}
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                                onClick={editTable}
                                disabled={saving}
                            >
                                {saving ? 'Saving…' : 'Save changes'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Club;
